import { useEffect, useRef, useState } from 'react';
// Import from the package root. CRA's TypeScript (moduleResolution: "node")
// doesn't read package.json "exports" subpath types, so "scorm-again/scorm2004"
// and "scorm-again/cross-frame-lms" can't be resolved. The package's root
// index.d.ts re-exports both as named exports (see scorm-again@3.0.3).
import { Scorm2004API, CrossFrameLMS } from 'scorm-again';
import { Box, CircularProgress, Typography } from '../../ui';

interface ScormViewerProps {
  scormPackageUrl?: string | null;
  scormEntryPoint?: string | null;
  onComplete?: () => void;
}

/**
 * SCORM 2004 player.
 *
 * Architecture: we don't iframe the SCORM package directly. Instead we iframe
 * a shared launcher HTML file (hosted once per blob container at
 * `public/scorm/_launcher.html`, not per account — packages themselves live
 * under `public/accounts/{account}/scorm/{guid}/`). The launcher installs
 * `window.API_1484_11` on its
 * own window via scorm-again's CrossFrameAPI, then nested-iframes the untouched
 * SCORM package. The package's Rustici scormdriver walks `window.parent` and
 * finds the API on the launcher — normal SCORM handshake. The launcher's
 * CrossFrameAPI forwards every call up one more level via postMessage to this
 * parent frame, where CrossFrameLMS routes it to the real Scorm2004API.
 *
 * Why the launcher layer: the SCORM package on blob is cross-origin from this
 * app, so the package can't directly access our `window.API_1484_11`. The
 * launcher lives on the same origin as the package (both on blob), bridging
 * the same-origin constraint for the inner SCO while using postMessage to
 * bridge the cross-origin gap between launcher and us.
 *
 * Completion signal: we listen for cmi.completion_status and cmi.success_status
 * changes. When either flips to "completed" or "passed", we fire onComplete,
 * which routes through LessonViewer's existing onLessonComplete plumbing to
 * hit the same markLessonComplete endpoint every other lesson type uses.
 */
export default function ScormViewer({ scormPackageUrl, scormEntryPoint, onComplete }: ScormViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const completedRef = useRef(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!scormPackageUrl || !scormEntryPoint) return;
    setLoading(true);

    // Compose URLs. Packages are nested per account, e.g.
    // "https://.../public/accounts/{account}/scorm/{guid}/". The launcher is
    // hosted ONCE per blob container at "https://.../public/scorm/_launcher.html"
    // (NOT per account), so we anchor it at the "public/" segment and point at
    // the shared "scorm/_launcher.html" there, rather than walking up from the
    // per-account package path.
    const packageBase = scormPackageUrl.endsWith('/') ? scormPackageUrl : scormPackageUrl + '/';
    const packageEntry = new URL('.' + scormEntryPoint, packageBase).toString();

    const packageUrl = new URL(packageBase);
    const marker = '/public/';
    const markerIdx = packageUrl.pathname.indexOf(marker);
    const launcherBase =
      markerIdx >= 0
        ? `${packageUrl.origin}${packageUrl.pathname.slice(0, markerIdx + marker.length)}scorm/`
        : new URL('..', packageBase).toString(); // fallback: one level up
    const launcherUrl = `${launcherBase}_launcher.html?src=${encodeURIComponent(packageEntry)}`;

    const launcherOrigin = new URL(launcherUrl).origin;

    // 1. Set up the real SCORM 2004 API on this window.
    const api = new Scorm2004API({
      autocommit: true,
      logLevel: 4, // warn+error only
    });

    // 2. Wrap it with CrossFrameLMS so the launcher's postMessage calls are
    //    routed to the real API. Validate incoming messages originate from
    //    the blob host we host the launcher on — the launcher itself uses
    //    origin '*' on its side because tenant origins are dynamic, but the
    //    blob origin is fixed so we CAN tighten on our end.
    const bridge = new CrossFrameLMS(api, launcherOrigin);

    // 3. Watch for completion signals. SCORM 2004 has two orthogonal status
    //    fields: completion_status (did they finish the content?) and
    //    success_status (did they pass an assessment?). For CE content we
    //    treat either a "completed" or a "passed" as lesson-complete.
    const fireComplete = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      try { onComplete?.(); } catch (e) { console.error('SCORM completion callback failed', e); }
    };

    const checkStatus = () => {
      try {
        const completion = api.cmi?.completion_status;
        const success = api.cmi?.success_status;
        if (completion === 'completed' || success === 'passed') fireComplete();
      } catch { /* api not ready yet */ }
    };

    // scorm-again emits events for SetValue and Commit/Terminate. Hook them all.
    api.on('SetValue.cmi.completion_status', checkStatus);
    api.on('SetValue.cmi.success_status', checkStatus);
    api.on('Commit', checkStatus);
    api.on('Terminate', checkStatus);

    // 4. Create the iframe pointing at the launcher.
    const frame = document.createElement('iframe');
    frame.src = launcherUrl;
    frame.title = 'SCORM content';
    frame.setAttribute('allowfullscreen', '');
    frame.setAttribute('allow', 'autoplay; fullscreen');
    frame.style.cssText = 'width:100%;height:100%;border:0;display:block;';
    // The launcher's load event waits for the nested package frame it creates
    // while parsing, so this marks the content itself as ready.
    frame.addEventListener('load', () => setLoading(false));

    const container = containerRef.current;
    container?.appendChild(frame);

    return () => {
      try { frame.remove(); } catch { /* ignore */ }
      try { (bridge as any)?.dispose?.(); } catch { /* scorm-again cleanup if available */ }
      try { api.terminate?.('', false); } catch { /* may already be terminated */ }
    };
  }, [scormPackageUrl, scormEntryPoint, onComplete]);

  if (!scormPackageUrl || !scormEntryPoint) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No SCORM package uploaded yet. Upload a .zip on the lesson edit screen to play this lesson.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '80vh', minHeight: 600 }}>
      <Box ref={containerRef} sx={{ width: '100%', height: '100%' }} />
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}
    </Box>
  );
}
